import { Maybe } from '@passionware/monads';
import { isUrlValid } from 'v1/helpers/urlHelpers';

/**
 * Abstract interface for link resolvers
 * A link resolver takes an input string and returns structured information
 */
export type AbstractLinkResolver = {
  /**
   * Parse the input string and return the platform and handle
   * @param inputString - The input string to parse
   * @returns The platform and handle
   */
  parseInput: (inputString: string) => Maybe<AbstractLinkParseResult>;

  /**
   * Get matching score for the input string (0-1 scale)
   * 0 = definitely not this platform
   * 1 = definitely this platform
   * @param inputString - The input string to score
   * @returns Score between 0 and 1
   */
  getMatchingScore: (inputString: string) => number;
};

/** Who a profile link points at, and the bare name to show. */
export type SocialProfile = { kind: 'person' | 'company'; name: string };

/**
 * Result of parsing a social media link
 */
export type AbstractLinkParseResult = {
  handle: string;
  fullUrl: string;
  /**
   * A profile the link names (LinkedIn: a person or a company), for display with
   * its icon; `null` when the input names none.
   */
  profile?: SocialProfile | null;
};

/**
 * Options for creating a platform-aware parser
 */
export type PlatformAwareParserOptions = {
  /**
   * Platform base URLs (e.g. 'twitter.com/', 'x.com/'); the first is the one
   * a link built from a handle uses. None: the parser reads nothing ('other').
   */
  platformBases: ReadonlyArray<string>;

  /**
   * Function to parse a full URL when platform base is detected
   * @param inputString - The full URL input
   * @param normalizedInput - The normalized (lowercase) input
   * @param platformBase - The platform base URL
   * @param normalizedBase - The normalized platform base URL
   * @returns Parsed result or null if URL is invalid
   */
  parseFullUrl: (
    inputString: string,
    normalizedInput: string,
    platformBase: string,
    normalizedBase: string
  ) => Maybe<AbstractLinkParseResult>;

  /**
   * Function to parse a handle (non-URL input)
   * @param inputString - The handle input
   * @param normalizedInput - The normalized (lowercase) input
   * @returns Parsed result
   */
  parseHandle: (
    inputString: string,
    normalizedInput: string
  ) => AbstractLinkParseResult;

  /**
   * When set, host-shaped input without `http(s)://` and without `platformBase` may still be parsed
   * as a handle if `maxInputScoring` is positive (e.g. Instagram dotted usernames).
   */
  inputScoring?: Maybe<Map<RegExp, number>>;
};

/**
 * Options for creating a platform-aware scorer
 */
export type PlatformAwareScorerOptions = {
  /** As `PlatformAwareParserOptions.platformBases`; none: the lowest score (0.1, 'other'). */
  platformBases: ReadonlyArray<string>;

  /**
   * Regex → score. The **first capturing group** (when present) is the extracted handle; if a
   * pattern has no group, the full match is the handle. Score is the max among matching patterns;
   * on a tie, the earlier Map entry wins.
   */
  inputScoring: Maybe<Map<RegExp, number>>;
};

/**
 * Creates a reusable parser that handles the common control flow:
 * - Detects if input is a full URL or handle
 * - Validates platform base for full URLs
 * - Delegates to specific parsing functions
 *
 * @param options - Configuration options for the parser
 * @returns A parseInput function
 */
export const createPlatformAwareParser = (
  options: PlatformAwareParserOptions
) => {
  return (inputString: string): Maybe<AbstractLinkParseResult> => {
    const { platformBases, parseFullUrl, parseHandle, inputScoring } = options;

    // Skip parsing without a base (e.g., 'other' platform)
    if (platformBases.length === 0) {
      return null;
    }

    // Check whether input looks like a URL (with or without protocol)
    const isValidUrl = isUrlValid(inputString);
    const normalizedInput = inputString.toLowerCase();
    const linkedBase = findPlatformBase(normalizedInput, platformBases);
    const platformBase = linkedBase ?? platformBases[0];
    const normalizedBase = platformBase.toLowerCase();
    const hasPlatformBase = linkedBase !== null;

    // Input starts with http:// or https://:
    // treat it as an explicit URL and accept only links for this platform.
    if (hasHttpOrHttpsProtocol(inputString)) {
      if (!hasPlatformBase) {
        // This is URL for different platform
        return null;
      }
      // Platform URL recognized, delegate to parseFullUrl
      return parseFullUrl(
        inputString,
        normalizedInput,
        platformBase,
        normalizedBase
      );
    }

    // Input has no protocol but still looks like a URL and already contains this platform base
    // (e.g. "instagram.com/foo"), so parse it as a full platform URL.
    if (isValidUrl && hasPlatformBase) {
      return parseFullUrl(
        inputString,
        normalizedInput,
        platformBase,
        normalizedBase
      );
    }

    if (isValidUrl && !hasPlatformBase) {
      const scoring = inputScoring ?? null;
      if (scoring && scoring.size > 0) {
        const score = maxInputScoring(inputString, scoring);
        if (score > 0) {
          return parseHandle(inputString, normalizedInput);
        }
      }
      return null;
    }

    return parseHandle(inputString, normalizedInput);
  };
};

/**
 * Creates a reusable scorer that handles the common control flow:
 * - Detects if input is a full URL or handle
 * - Validates platform base for full URLs
 * - Scores based on input regex map
 *
 * @param options - Configuration options for the scorer
 * @returns A getMatchingScore function
 */
export const createPlatformAwareScorer = (
  options: PlatformAwareScorerOptions
) => {
  return (inputString: string): number => {
    const { platformBases, inputScoring } = options;

    if (platformBases.length === 0) {
      // 'other' platform - lowest score
      return 0.1;
    }

    const isValidUrl = isUrlValid(inputString);
    const normalizedInput = inputString.toLowerCase();
    const hasPlatformBase =
      findPlatformBase(normalizedInput, platformBases) !== null;

    // Input starts with http:// or https://:
    // do not use broad (unanchored) regex heuristics to avoid false positives inside long URLs.
    if (hasHttpOrHttpsProtocol(inputString)) {
      if (hasPlatformBase) {
        return 1;
      }
      if (inputScoring && inputScoring.size > 0) {
        const anchored = maxAnchoredInputScoring(inputString, inputScoring);
        if (anchored > 0) {
          return anchored;
        }
      }
      return 0;
    }

    // Input has no protocol:
    // first prefer direct platform URL matches (e.g. "instagram.com/foo"),
    // then fallback to inputScoring heuristics (e.g. "ig:foo", "john.doe").
    if (isValidUrl && hasPlatformBase) {
      return 1;
    }

    if (inputScoring && inputScoring.size > 0) {
      const maxScore = maxInputScoring(inputString, inputScoring);
      if (maxScore > 0) {
        return maxScore;
      }
    }

    if (isValidUrl && !hasPlatformBase) {
      return 0;
    }

    // Plain handle without prefix - possible but uncertain
    return 0.3;
  };
};

/**
 * The base the (lowercased) input links to: one that starts the input, a path
 * or a host label, so “netflix.com/jane” is no “x.com/” link.
 */
export function findPlatformBase(
  normalizedInput: string,
  platformBases: ReadonlyArray<string>
): string | null {
  return (
    platformBases.find(base =>
      new RegExp(
        `(?:^\\s*|[/.])${base.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`
      ).test(normalizedInput)
    ) ?? null
  );
}

const cloneRegexWithoutGlobal = (pattern: RegExp): RegExp =>
  new RegExp(pattern.source, pattern.flags.replace(/g/g, ''));

/**
 * Highest score among all map entries whose pattern matches the input.
 */
export function maxInputScoring(
  inputString: string,
  scoring: Map<RegExp, number>
): number {
  let best = 0;
  for (const [pattern, score] of scoring) {
    const testRe = cloneRegexWithoutGlobal(pattern);
    if (testRe.test(inputString)) {
      best = Math.max(best, score);
    }
  }
  return best;
}

/**
 * Like `maxInputScoring`, but only patterns whose source starts with `^` (anchored). Safe to use on
 * `https://…` strings so unanchored heuristics do not match substrings inside the URL.
 */
export function maxAnchoredInputScoring(
  inputString: string,
  scoring: Map<RegExp, number>
): number {
  let best = 0;
  for (const [pattern, score] of scoring) {
    if (!pattern.source.startsWith('^')) {
      continue;
    }
    const testRe = cloneRegexWithoutGlobal(pattern);
    if (testRe.test(inputString)) {
      best = Math.max(best, score);
    }
  }
  return best;
}

const hasHttpOrHttpsProtocol = (input: string): boolean =>
  /^https?:\/\//i.test(input.trim());

/**
 * Picks the highest-scoring pattern that matches `inputString` (ties → first in Map order),
 * then returns the first capture group if the regex defines one, otherwise the full match.
 * If nothing matches or `scoring` is empty, returns the trimmed input.
 */
export function extractHandleFromInputScoring(
  inputString: string,
  scoring: Map<RegExp, number>
): string {
  if (!scoring.size) {
    return inputString.trim();
  }

  let bestScore = -1;
  let bestPattern: RegExp | null = null;

  for (const [pattern, score] of scoring) {
    const testRe = cloneRegexWithoutGlobal(pattern);
    if (testRe.test(inputString)) {
      if (score > bestScore) {
        bestScore = score;
        bestPattern = pattern;
      }
    }
  }

  if (bestPattern === null) {
    return inputString.trim();
  }

  const execRe = cloneRegexWithoutGlobal(bestPattern);
  const m = execRe.exec(inputString);
  if (!m) {
    return inputString.trim();
  }

  if (m.length > 1 && m[1] !== undefined) {
    return m[1];
  }

  return m[0];
}
